/**
 * How to read a `payout_transactions` row's money: ONE rule for every reader (web, mobile,
 * edge), so a row shows the same figure everywhere.
 *
 * Two conventions exist for `amount_cents`:
 *
 * - STAMPED GROSS (`metadata.amount_convention = 'GROSS'`): `amount_cents` is what moved on
 *   Stripe, GST-inclusive, for every transaction type (negative for refunds and clawbacks), and
 *   `gst_amount_cents` is the GST portion of it (same sign). Writers stamp new rows this way, and
 *   the S3 ledger backfill (PR #3480 (refresh of #3420), migration 20261007020000) converts and stamps old rows.
 * - UNSTAMPED (legacy): the per-type rule every reader applied before S3. MILESTONE_RELEASE,
 *   VARIATION_RELEASE and CLAWBACK rows store the EX-GST amount with the GST in
 *   `gst_amount_cents`, so the gross is `amount + gst`. Every other type already stores the
 *   gross (the Stripe transfer amount); adding its `gst_amount_cents` would count the GST twice.
 *   This is exactly the set the backfill converts, so a row reads the same before and after it is
 *   stamped.
 *
 * Readers MUST go through `payoutGrossCents` / `payoutAmountIsExGst` instead of testing the type
 * themselves: installed mobile builds cannot be updated over the air, so a reader that knows only
 * the legacy rule would count the GST twice on every stamped row.
 *
 * Zero dependencies: re-exported for Deno edge functions by supabase/functions/_shared/payout-amount.ts.
 */

/** The `metadata.amount_convention` value that marks a row whose `amount_cents` is gross. */
export const PAYOUT_AMOUNT_CONVENTION_GROSS = 'GROSS';

/**
 * Every `transaction_type` that CHECK `payout_transactions_transaction_type_check` allows (live on
 * prod 2026-09-29; last defined by migration 20260918190000). A parity test parses the latest
 * migration defining that CHECK and fails if the two lists differ, so a new type cannot reach a
 * reader without an explicit rule below.
 */
export const PAYOUT_TRANSACTION_TYPES = [
  'INITIAL_PAYOUT',
  'INITIAL_RELEASE',
  'FINAL_RELEASE',
  'CANCELLATION_COMPENSATION',
  'VARIATION_RELEASE',
  'DEDUCTION_REVERSAL',
  'ON_SITE_INITIAL_RELEASE',
  'MILESTONE_RELEASE',
  'INSPECTOR_SPLIT',
  'CLAWBACK',
  'HELD_REFUND',
  'ADMIN_REFUND',
] as const;

export type PayoutTransactionType = (typeof PAYOUT_TRANSACTION_TYPES)[number];

/**
 * How an UNSTAMPED row of each type stores `amount_cents`:
 * - `EX_GST`: net of GST; the gross is `amount_cents + gst_amount_cents`.
 * - `GROSS`: already GST-inclusive; the gross is `amount_cents`.
 */
export type LegacyPayoutAmountBasis = 'EX_GST' | 'GROSS';

export const LEGACY_PAYOUT_AMOUNT_BASIS: Readonly<Record<PayoutTransactionType, LegacyPayoutAmountBasis>> = {
  // Written ex-GST before S3 (approve_variation_payout / release_milestone_payout / dispute and
  // variation-rejection clawbacks). The S3 backfill converts exactly these three types.
  MILESTONE_RELEASE: 'EX_GST',
  VARIATION_RELEASE: 'EX_GST',
  CLAWBACK: 'EX_GST',
  // The Stripe transfer amount, GST-inclusive.
  INITIAL_PAYOUT: 'GROSS',
  INITIAL_RELEASE: 'GROSS',
  FINAL_RELEASE: 'GROSS',
  ON_SITE_INITIAL_RELEASE: 'GROSS',
  INSPECTOR_SPLIT: 'GROSS',
  DEDUCTION_REVERSAL: 'GROSS',
  // create_cancellation_invoice_and_compensation writes amount = compensation + GST (gross);
  // cancel-job's RECONCILE_REQUIRED path writes GST 0.
  CANCELLATION_COMPENSATION: 'GROSS',
  // Client refunds: the (negative) amount refunded to the client; admin-refund-payout writes GST 0.
  HELD_REFUND: 'GROSS',
  ADMIN_REFUND: 'GROSS',
};

/** A payout_transactions row, or the subset of it a reader selected. */
export interface PayoutAmountRow {
  transaction_type?: string | null;
  amount_cents?: number | null;
  gst_amount_cents?: number | null;
  /** `metadata->>amount_convention`, when the reader selected it on its own. */
  amount_convention?: string | null;
  /** The row's `metadata`; `metadata.amount_convention` is read when `amount_convention` is absent. */
  metadata?: unknown;
}

/**
 * The legacy basis for a transaction type, or `undefined` for a type the table does not name.
 * Readers treat an unnamed type as GROSS (what every pre-S3 reader did for a type outside the
 * ex-GST set; synthetic labels such as the mobile card's `SETTLEMENT_RELEASE` reach the remittance
 * generator this way). Every type the database allows is named: the parity test enforces it.
 */
export function legacyPayoutAmountBasis(transactionType: string | null | undefined): LegacyPayoutAmountBasis | undefined {
  if (!transactionType) return undefined;
  return Object.prototype.hasOwnProperty.call(LEGACY_PAYOUT_AMOUNT_BASIS, transactionType)
    ? LEGACY_PAYOUT_AMOUNT_BASIS[transactionType as PayoutTransactionType]
    : undefined;
}

/** The row's `amount_convention` marker (from `amount_convention`, else `metadata.amount_convention`). */
export function payoutAmountConvention(row: PayoutAmountRow | null | undefined): string | null {
  if (!row) return null;
  if (typeof row.amount_convention === 'string') return row.amount_convention;
  const metadata = row.metadata;
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    const value = (metadata as Record<string, unknown>).amount_convention;
    if (typeof value === 'string') return value;
  }
  return null;
}

/** True when the row is stamped `amount_convention = 'GROSS'`: `amount_cents` is the gross. */
export function isGrossStampedPayout(row: PayoutAmountRow | null | undefined): boolean {
  return payoutAmountConvention(row) === PAYOUT_AMOUNT_CONVENTION_GROSS;
}

/**
 * True when the row's `amount_cents` is EX-GST, i.e. an unstamped MILESTONE_RELEASE /
 * VARIATION_RELEASE / CLAWBACK row. Its gross is `amount_cents + gst_amount_cents`.
 */
export function payoutAmountIsExGst(row: PayoutAmountRow | null | undefined): boolean {
  if (!row || isGrossStampedPayout(row)) return false;
  return legacyPayoutAmountBasis(row.transaction_type) === 'EX_GST';
}

/**
 * The row's gross (GST-inclusive) cents — what moved on Stripe for it: `amount_cents` for a
 * stamped row, else the legacy per-type rule. Signed like `amount_cents` (negative for refunds
 * and clawbacks). The GST portion is always `gst_amount_cents`.
 */
export function payoutGrossCents(row: PayoutAmountRow | null | undefined): number {
  if (!row) return 0;
  const amount = row.amount_cents ?? 0;
  return payoutAmountIsExGst(row) ? amount + (row.gst_amount_cents ?? 0) : amount;
}

/**
 * True when a job has a valid locked `trade_net_cents` snapshot.
 * False when `tradeNetCents` is null/undefined, non-finite, negative, or 0 while
 * `clientTotalCents ?? 0` is > 0.
 */
export function hasTradeShareSnapshot(
  tradeNetCents: number | null | undefined,
  clientTotalCents?: number | null,
): tradeNetCents is number {
  if (
    tradeNetCents == null ||
    !Number.isFinite(tradeNetCents) ||
    tradeNetCents < 0 ||
    (tradeNetCents === 0 && (clientTotalCents ?? 0) > 0)
  ) {
    return false;
  }
  return true;
}

export interface FinalReleaseCompletedRow extends PayoutAmountRow {
  id?: string | null;
}

export interface FinalReleaseInspectorSplitRow {
  id?: string | null;
  amount_cents?: number | null;
  status?: string | null;
}

export interface ComputeFinalReleaseBaseInput {
  tradeNetCents: number;
  tradeGstCents: number;
  completedRows?: readonly FinalReleaseCompletedRow[] | null;
  inspectorSplitRows?: readonly FinalReleaseInspectorSplitRow[] | null;
}

export interface FinalReleaseBase {
  tradeShareCents: number;
  alreadyPaidCents: number;
  holdbackCents: number;
  holdbackGstCents: number;
  gstAlreadyPaidCents: number;
}

function toInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return parseInt(value.trim(), 10);
  return null;
}

function toNonNegativeInt(value: unknown): number {
  const n = toInt(value);
  return n != null && n > 0 ? n : 0;
}

/**
 * Computes the pre-refund-adjustment final release base (`share - already paid` and its GST
 * counterpart) from the locked trade net + GST and the completed / inspector-split payout rows.
 */
export function computeFinalReleaseBase({
  tradeNetCents,
  tradeGstCents,
  completedRows,
  inspectorSplitRows,
}: ComputeFinalReleaseBaseInput): FinalReleaseBase {
  const tradeShareCents = tradeNetCents + tradeGstCents;

  const inspectorSplitIds = new Set<string>();
  let totalInspectorSplitCents = 0;
  for (const split of inspectorSplitRows ?? []) {
    // A FAILED split never moved money; counting it would double a FAILED + live pair (#3603 F6).
    if (split.status === 'FAILED' || split.status === 'CANCELLED') continue;
    if (split.id) inspectorSplitIds.add(split.id);
    totalInspectorSplitCents += split.amount_cents ?? 0;
  }

  // amount_cents IS the Stripe transfer amount and already includes the GST
  // portion. gst_amount_cents is a label for the GST subset within
  // amount_cents — do NOT add them together (that double-counts).
  //
  // locked_figure_counts_withheld_deductions (Max, 2026-09-22): a deduction withheld
  // from a job's initial payout, or an inspector split taken from it, counts as already
  // paid out of the trade's share; deduction reversals and cancellation compensations
  // are excluded from payouts made.
  const totalPaidFromRows =
    completedRows?.reduce((sum, tx) => {
      // Exclude VARIATION_RELEASE payouts from the main job's remaining payout calculation
      if (tx.transaction_type === 'VARIATION_RELEASE') {
        return sum;
      }
      // MONEY PATH. Refund and clawback rows are NEGATIVE. Summed in here they would make
      // "already paid" smaller and the final release LARGER — refunding a client would
      // pay the trade more, by exactly the refunded amount. They are not payouts, so they
      // never count towards what has been paid (same exclusion as the locked figure in
      // get_trade_financial_dashboard). The amount guard covers any future negative type.
      if (
        tx.transaction_type === 'HELD_REFUND' ||
        tx.transaction_type === 'ADMIN_REFUND' ||
        tx.transaction_type === 'CLAWBACK' ||
        payoutGrossCents(tx) < 0
      ) {
        return sum;
      }
      // Exclude DEDUCTION_REVERSAL and CANCELLATION_COMPENSATION: they are not payouts
      // of this job's share.
      if (
        tx.transaction_type === 'DEDUCTION_REVERSAL' ||
        tx.transaction_type === 'CANCELLATION_COMPENSATION'
      ) {
        return sum;
      }
      // An INSPECTOR_SPLIT row on the job is counted once (either here or via inspectorSplits).
      if (tx.transaction_type === 'INSPECTOR_SPLIT') {
        if (tx.id && !inspectorSplitIds.has(tx.id)) {
          inspectorSplitIds.add(tx.id);
          return sum + payoutGrossCents(tx);
        }
        return sum;
      }

      let rowTotal = payoutGrossCents(tx);

      // A deduction withheld from this job's initial payout settled a debt of the trade
      // using its share of this job; it counts as already paid to the trade.
      if (
        tx.transaction_type === 'INITIAL_RELEASE' ||
        tx.transaction_type === 'INITIAL_PAYOUT' ||
        tx.transaction_type === 'ON_SITE_INITIAL_RELEASE'
      ) {
        const meta =
          tx.metadata && typeof tx.metadata === 'object' && !Array.isArray(tx.metadata)
            ? (tx.metadata as Record<string, unknown>)
            : {};
        rowTotal += toNonNegativeInt(meta.deduction_applied_cents);
      }

      return sum + rowTotal;
    }, 0) || 0;

  const alreadyPaidCents = totalPaidFromRows + totalInspectorSplitCents;
  const holdbackCents = tradeShareCents - alreadyPaidCents;

  // GST labelled on the payouts already made — the same rows as totalPaidCents. Refund and
  // clawback rows are not payouts, so any GST they carry is not "GST already paid" either.
  const gstAlreadyPaidCents =
    completedRows
      ?.filter(
        (tx) =>
          tx.transaction_type !== 'VARIATION_RELEASE' &&
          tx.transaction_type !== 'HELD_REFUND' &&
          tx.transaction_type !== 'ADMIN_REFUND' &&
          tx.transaction_type !== 'CLAWBACK' &&
          tx.transaction_type !== 'DEDUCTION_REVERSAL' &&
          tx.transaction_type !== 'CANCELLATION_COMPENSATION' &&
          tx.transaction_type !== 'INSPECTOR_SPLIT' &&
          (tx.amount_cents ?? 0) >= 0,
      )
      .reduce((sum, tx) => sum + (tx.gst_amount_cents ?? 0), 0) || 0;

  const holdbackGstCents = Math.max(0, tradeGstCents - gstAlreadyPaidCents);

  return {
    tradeShareCents,
    alreadyPaidCents,
    holdbackCents,
    holdbackGstCents,
    gstAlreadyPaidCents,
  };
}

